import { useCallback, useEffect, useLayoutEffect } from "react";

import { useShare } from "@keepeek/commons";
import { useMediaQuery, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { SetterOrUpdater, useRecoilState } from "recoil";

import { KHeaderWidget } from "../../../../models/configuration/components/kHeaderWidget";
import { usePageVisible } from "../../../../providers/config/hooks";
import { kHeaderWidgetComponentsAtom } from "../atoms";
import { KHeaderWidgetComponent, KHeaderWidgetComponentContext } from "../models";
import { UseKHeaderWidgetSelect, useKHeaderWidgetSelect } from "./useKHeaderWidgetSelect";

export type UseKHeaderWidgetComponents = {
  currentComponents: KHeaderWidgetComponent[];
  burgerComponents: KHeaderWidgetComponent[];
  topBarComponents: KHeaderWidgetComponent[];
  setCurrentComponents: SetterOrUpdater<KHeaderWidgetComponent[]>;
  setBurgerComponents: SetterOrUpdater<KHeaderWidgetComponent[]>;
  setTopBarComponents: SetterOrUpdater<KHeaderWidgetComponent[]>;
  selectComponents: (context: KHeaderWidgetComponentContext) => KHeaderWidgetComponent[];
  configuration: UseKHeaderWidgetSelect;
};

type UseKHeaderWidgetComponentsProps = {
  widgetId: KHeaderWidget["id"];
  barWidth: number | undefined;
};

export const useKHeaderWidgetComponents = ({
  widgetId,
  barWidth,
}: UseKHeaderWidgetComponentsProps): UseKHeaderWidgetComponents => {
  // GETTER
  const [currentComponents, setCurrentComponents] = useRecoilState(
    kHeaderWidgetComponentsAtom({ widgetId, context: KHeaderWidgetComponentContext.All }),
  );

  const [burgerComponents, setBurgerComponents] = useRecoilState(
    kHeaderWidgetComponentsAtom({ widgetId, context: KHeaderWidgetComponentContext.Burger }),
  );

  const [topBarComponents, setTopBarComponents] = useRecoilState(
    kHeaderWidgetComponentsAtom({ widgetId, context: KHeaderWidgetComponentContext.TopBar }),
  );

  const selectComponents: UseKHeaderWidgetComponents["selectComponents"] = useCallback(
    (context) => {
      switch (context) {
        case KHeaderWidgetComponentContext.TopBar:
          return topBarComponents;
        case KHeaderWidgetComponentContext.Burger:
          return burgerComponents;
        default:
          return currentComponents;
      }
    },
    [burgerComponents, currentComponents, topBarComponents],
  );

  // SETTER
  const configuration = useKHeaderWidgetSelect({ widgetId });
  const { homeVisible, elementVisible, searchVisible } = usePageVisible();
  const router = useRouter();
  const { isShare } = useShare(router.pathname, router.query.tokenId);

  // Set the "All" components
  useEffect(() => {
    if (configuration) {
      const { kHeaderWidgetActions } = configuration;

      const components: KHeaderWidgetComponent[] = [];
      // Custom links
      if (
        !isShare &&
        kHeaderWidgetActions?.kHeaderWidgetActionsCustomLinks &&
        kHeaderWidgetActions?.kHeaderWidgetActionsCustomLinks.length > 0
      ) {
        components.push(KHeaderWidgetComponent.CustomNavigation);
      }
      // Home Button
      if (
        !isShare &&
        homeVisible &&
        (elementVisible || searchVisible) &&
        kHeaderWidgetActions?.kHeaderWidgetActionsHome?.active
      ) {
        components.push(KHeaderWidgetComponent.Home);
      }
      // Search
      if (!isShare && kHeaderWidgetActions?.kHeaderWidgetActionsSearchBar?.active) {
        components.push(KHeaderWidgetComponent.SearchBar);
      }
      // User
      if (!isShare && kHeaderWidgetActions?.kHeaderWidgetActionsUser?.active) {
        components.push(KHeaderWidgetComponent.User);
      }
      // Lang selector
      if (kHeaderWidgetActions?.kHeaderWidgetActionsLangSelector?.active) {
        components.push(KHeaderWidgetComponent.LangSelector);
      }
      // Basket
      if (!isShare && kHeaderWidgetActions?.kHeaderWidgetActionsBasket?.active) {
        components.push(KHeaderWidgetComponent.Basket);
      }
      setCurrentComponents(components);
    }
  }, [configuration, elementVisible, homeVisible, isShare, searchVisible, setCurrentComponents]);

  // Set the "TopBar" components
  useEffect(() => {
    // Take the components from "All" and substract the ones in burger
    const components = currentComponents.filter((c) => !burgerComponents.includes(c));
    setTopBarComponents(components);
  }, [burgerComponents, currentComponents, setTopBarComponents]);

  // Set the "burger" components in
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isHeaderMdDown = barWidth ? barWidth < theme.breakpoints.values.md : isMdDown;

  useLayoutEffect(() => {
    if (isHeaderMdDown) {
      setBurgerComponents(currentComponents.filter((c) => c !== KHeaderWidgetComponent.Basket));
    } else {
      setBurgerComponents([]);
    }
  }, [currentComponents, isHeaderMdDown, isMdDown, setBurgerComponents]);

  return {
    currentComponents,
    burgerComponents,
    topBarComponents,
    selectComponents,
    setCurrentComponents,
    setBurgerComponents,
    setTopBarComponents,
    configuration,
  };
};
